import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import AlternativeProductsForProductListQuery from '../../../graphql/queries/product/AlternativeProductsForProductListQuery.graphql';
import StockTotalQuery from '../../../graphql/queries/product/StockTotalQuery.graphql';
import { CompoundButton, InfoButton, WarningButton } from '../../connectedForm/Button';
import setIn from '../../connectedForm/utils/setIn';
import RaisePurchaseOrderFromSalesOrderLine from '../../../graphql/mutations/purchases/RaisePurchaseOrderFromSalesOrderLine.graphql';
import RaiseAssemblyFromSalesOrderLine from '../../../graphql/mutations/product/RaiseAssemblyFromSalesOrderLine.graphql';
import PurchaseOrderLineAssignmentListQuery from '../../../graphql/queries/purchases/PurchaseOrderLineAssignmentListQuery.graphql';
import normalizeVariables from '../../../graphql/schema/normalizeVariables';
import { ModalBottomButtonContainer } from '../Styles';
import { default as Form } from '../../connectedForm';
import { useComponentQueryErrorHandling } from '../../../utils/parseErrorResponse';
import { checkLineCanProductChange } from '../../../utils/LineUtils';
// @ts-ignore
import { Analytics } from 'mixpanel';
import { InfoTheme } from '../../../styles/themes/default/defaultFluentTheme';
import getClientMutationId from '../../../utils/getClientMutationId';
import ConnectedFormContext from '../../Context/ConnectedFormContext';
import { usePurchaseOrderCapability } from '../usePurchaseOrderCapability';
import {
  Direction,
  FilterOperation,
  IAlternativeProductsDropdownQueryResult,
  IAlternativeProductsDropdownQueryVariables,
  IAssembly,
  IAssemblyQueryResult,
  IAssemblyQueryVariables,
  IPurchaseOrderLine,
  IPurchaseOrderLineAssignmentQueryResult,
  IPurchaseOrderLineAssignmentQueryVariables,
  IRaiseAssemblyFromSalesOrderLineMutationResult,
  IRaiseAssemblyFromSalesOrderLineMutationVariables,
  IRaisePurchaseOrderParametersInput,
  ISalesOrder,
  ISalesOrderInput,
  ISalesOrderLine,
  ISalesOrderLineInput,
  IStockTotalQueryResult,
  IStockTotalQueryVariables,
  ISupplier,
  Maybe,
  ProductClass,
} from '../../../graphql/schema/p4types';
import { Pivot, PivotItem } from '@fluentui/react';
import { BasicHeader } from '../../../actions/BasicHeader';
import { AssignPurchaseOrdersGrid, usePurchaseOrderAssignedTotal } from './AssignPurchaseOrdersGrid';
import { AlternativeProductsGrid } from './AlternativeProductsGrid';
import { RaiseShortageSection } from './RaiseShortageSection';
import { ButtonContainerDiv } from '../ButtonContainerDiv';
import AssemblyModal from '../../../views/product/AssemblyModal';
import AssemblyQuery from '../../../graphql/queries/product/AssemblyQuery.graphql';
import { cloneDeep } from '@apollo/client/utilities';
import { pristineEqualsCheck } from '../../connectedForm/Hooks/pristineEntityHook';
import { usePerformSave } from '../../connectedForm/Hooks/usePerformSave';
import { useSalesOrderConfig } from '../../../utils/FetchConfig';
import { isPendingPriceApproval } from '../../../utils/SalesUtils';
import { useManualPricingRequiresApproval } from '../../../views/salesLedger/tradeCounter/hooks';
import FluentModal from '../FluentModal';
import { useFullUserDetails } from '../../../utils/hooks';

type IAlternativeProductModalProps = {
  value?: ISalesOrderLine;
  onChange: (newValue: ISalesOrderLine) => void;
  onClose: () => void;
  salesOrder?: ISalesOrder;
  onReviewLater: () => void;
  onRowClick?: Function;
  setRaiseShortage: Function;
  rowIdx: number;
  short: number;
  readonly isAssembly: boolean;
};

/**
 * Wrapper around pristineEqualsCheck because it usually expects an object, but we're starting with an Array so need
 *  to do some additional legwork to match up the elements for comparison.
 * @param baseArray - The base array, i.e. 'baseValues.foo.bar'
 * @param newArray - The current array, i.e. 'values.foo.bar'
 * @param arrayType - The type of the elements in the array, i.e. 'ProductComponent'
 */
const entityArrayPristineEqualsCheck = (baseArray: Array<any>, newArray: Array<any>, arrayType: string): boolean => {
  const elementMap: { [uuid: string]: { baseElement: any; newElement: any } } = {};
  newArray.forEach((newElement: any) => (elementMap[newElement.uuid!] = { newElement, baseElement: null }));
  for (const baseElement of baseArray) {
    if (!elementMap[baseElement.uuid]) {
      //If this uuid isn't already in the element map, consider it dirty.
      return false;
    }
    elementMap[baseElement.uuid!].baseElement = baseElement;
  }
  return Object.entries(elementMap).every((entry) => {
    const [, { baseElement, newElement }] = entry;
    if (!baseElement) {
      //We've already checked for missing new elements in the loop above which is why there's no '!newElement' here.
      return false;
    }

    // Note - 'freeStock' ignored because it doesn't represent a user change, likely been updated during a temporary save.
    return pristineEqualsCheck(baseElement, newElement, arrayType, { ignoreFieldKeys: ['freeStock'] });
  });
};

const fluentSaveIcon = { iconName: 'save' };

const AlternativeProductModal = ({
  value: salesOrderLine,
  onChange,
  onClose,
  salesOrder,
  onReviewLater,
  onRowClick,
  setRaiseShortage,
  rowIdx,
  short,
  isAssembly,
}: IAlternativeProductModalProps) => {
  const connectedForm = useContext(ConnectedFormContext);
  const manualPricingRequiresApproval = useManualPricingRequiresApproval();

  const [assemblyModalOpen, setAssemblyModalOpen] = useState<boolean>(false);
  const [tempAssembly, setTempAssembly] = useState<IAssembly | null>(null);
  const [formLegend, setFormLegend] = useState<string>('Raise Purchase Order');
  const orderConfig = useSalesOrderConfig();
  const fullUserDetails = useFullUserDetails();

  const [performSave, saveConfirmationPrompt] = usePerformSave<ISalesOrder>();

  const componentsPristine = useMemo(() => {
    if (salesOrderLine?.id === null || salesOrderLine?.id === undefined) {
      //If the SalesOrderLine is new, we don't need to require a save because of the draft-state of the PO.
      return true;
    }

    const baseValues = connectedForm.baseValues as ISalesOrder;
    const components = salesOrderLine?.components ?? [];
    const baseComponents =
      baseValues?.orderLines?.find((orderLine) => orderLine?.uuid === salesOrderLine?.uuid)?.components ?? [];
    return entityArrayPristineEqualsCheck(components, baseComponents, 'ProductComponent');
  }, [connectedForm, salesOrderLine]);

  useMemo(() => {
    const legend =
      isAssembly && fullUserDetails?.flags?.assembliesEnabled
        ? 'Raise Assembly / Purchase Order'
        : 'Raise Purchase Order';
    setFormLegend(legend);
  }, [isAssembly]);

  const { data: stockTotalData } = useQuery<IStockTotalQueryResult, IStockTotalQueryVariables>(StockTotalQuery, {
    variables: {
      productId: salesOrderLine?.product?.id,
      warehouseId: salesOrderLine?.warehouse?.id,
    },
    fetchPolicy: 'network-only',
    skip: !salesOrderLine,
  });

  const {
    purchaseOrderState,
    setPurchaseOrderState,
    modals: purchaseOrderModal,
  } = usePurchaseOrderCapability({
    onChange: onChange,
    onClose: onClose,
    value: salesOrderLine,
  });

  const handleQueryError = useComponentQueryErrorHandling();

  const [raisePurchaseOrder] = useMutation(RaisePurchaseOrderFromSalesOrderLine, {
    onCompleted: (data) => {
      const purchaseOrder = data?.raisePurchaseOrderFromSalesOrderLine?.result;
      if (purchaseOrder?.uuid) {
        setPurchaseOrderState({ modalOpen: true, initialValues: purchaseOrder });
      }
    },
    onError: handleQueryError,
  });

  const [raiseAssembly] = useMutation<
    IRaiseAssemblyFromSalesOrderLineMutationResult,
    IRaiseAssemblyFromSalesOrderLineMutationVariables
  >(RaiseAssemblyFromSalesOrderLine, {
    onError: handleQueryError,
  });

  const [getAssemblyQuery] = useLazyQuery<IAssemblyQueryResult, IAssemblyQueryVariables>(AssemblyQuery, {
    fetchPolicy: 'network-only',
    onError: handleQueryError,
    onCompleted: (data) => {
      if (data?.data) {
        const assembly = cloneDeep(data.data);
        assembly.quantity = salesOrderLine?.quantity;
        setTempAssembly(assembly as IAssembly);
        setAssemblyModalOpen(true);
      }
    },
  });

  const stockTotal = stockTotalData?.data;

  const freeStock: number = stockTotal?.freeStock ?? 0;
  const makeable: number = stockTotal?.makeable ?? 0;
  const totalAssigned = usePurchaseOrderAssignedTotal(salesOrderLine);
  const fullyAssigned = short === totalAssigned;

  //handle the click to raise an Assembly Order from a shortage
  const getRaiseAssemblyGetOnClick = (directDelivery = false, balanceOnly = true) => {
    Analytics.track('Raise Assembly From Shortage');

    if (salesOrderLine?.draftAssemblyId) {
      getAssemblyQuery({
        variables: {
          id: salesOrderLine?.draftAssemblyId,
        },
      }).then((data) => {
        raiseAssembly(
          normalizeVariables({
            variables: {
              input: {
                clientMutationId: getClientMutationId(),
                assemblyId: data?.data?.data?.id ?? null,
                balanceOnly,
                salesOrderLine: { ...salesOrderLine, salesOrder: salesOrder },
              },
            },
          })
        ).then((data: FetchResult<RaiseAssemblyFromSalesOrderLineResult>) => {
          //do this here
          if (data.data) {
            const assembly = data.data?.raiseAssemblyFromSalesOrderLine.result;
            setTempAssembly(assembly);
            setAssemblyModalOpen(true);
          }
        });
      });
    } else {
      raiseAssembly(
        normalizeVariables({
          variables: {
            input: {
              clientMutationId: getClientMutationId(),
              assemblyId: tempAssembly?.id ?? null,
              balanceOnly,
              salesOrderLine: { ...salesOrderLine, salesOrder: salesOrder },
            },
          },
        })
      ).then((data) => {
        //do this here
        if (data.data) {
          const assembly = data.data?.raiseAssemblyFromSalesOrderLine?.result as IAssembly;
          setTempAssembly(assembly);
          setAssemblyModalOpen(true);
        }
      });
    }
  };

  const raisePurchaseOrderFromLine = (
    salesOrderLine: ISalesOrderLine,
    raisePurchaseOrderParameters: IRaisePurchaseOrderParametersInput
  ) => {
    Analytics.track('Raise Purchase Order From Shortage', { ...raisePurchaseOrderParameters });

    raisePurchaseOrder(
      normalizeVariables({
        variables: {
          input: {
            clientMutationId: getClientMutationId(),
            supplier: null, //Require setting these on the PO Form.
            warehouse: null, //Require setting these on the PO Form.
            raisePurchaseOrderParameters,
            salesOrderLine: salesOrderLine as ISalesOrderLineInput,
          },
        },
      })
    ).then(() => {
      //Do nothing - Handled by 'onComplete' in useMutation definition.
    });
  };

  const getOnClick = (directDelivery = false, balanceOnly = true) => {
    const raisePurchaseOrderParameters: IRaisePurchaseOrderParametersInput = {
      directDelivery: directDelivery,
      balanceOnly: balanceOnly,
      directDeliveryOrder: salesOrder as ISalesOrderInput,
    };

    return () => {
      if (componentsPristine) {
        // Avoid passing to-be-deleted components up (Only afflicts lines that haven't yet been persisted) - PO does not need to be aware of them.
        raisePurchaseOrderFromLine(
          {
            ...(salesOrderLine as ISalesOrderLine),
            components: ((salesOrderLine as ISalesOrderLine)?.components ?? []).filter(
              (component) => !component?.deleted
            ),
            salesOrder,
          },
          raisePurchaseOrderParameters
        );
      } else {
        performSave(
          {
            submitMeta: {
              suppressAfterSubmit: true,
              suppressSuccessMessage: true,
              suppressActions: true, // Do not display print Modals from here - Defer to when the user does a regular save later.
            },
            onSaveComplete: ({ result }: { result: ISalesOrder }) => {
              const savedLine = result?.orderLines?.find(
                (persistedLine) => persistedLine?.uuid === salesOrderLine?.uuid
              );
              raisePurchaseOrderFromLine(
                { ...(savedLine as ISalesOrderLine), salesOrder: result },
                raisePurchaseOrderParameters
              );
            },
          },
          { ...connectedForm.values, delayAllocation: true }
        );
      }
    };
  };

  let editPurchaseOrderOnClick: Maybe<() => void> = null;
  if (salesOrderLine?.uuid && connectedForm.supportingData?.salesLineNewPurchaseOrderMap?.[salesOrderLine.uuid]) {
    const uuid: string = salesOrderLine.uuid;
    //If there is already a PO in the map for this line, load it up instead of re-generating a PO.
    editPurchaseOrderOnClick = () => {
      const existingPurchaseOrder = connectedForm.supportingData?.salesLineNewPurchaseOrderMap[uuid];
      setPurchaseOrderState({
        modalOpen: true,
        initialValues: existingPurchaseOrder,
      });
    };
  }

  const { data: purchaseOrderLinesData } = useQuery<
    IPurchaseOrderLineAssignmentQueryResult,
    IPurchaseOrderLineAssignmentQueryVariables
  >(PurchaseOrderLineAssignmentListQuery, {
    fetchPolicy: 'network-only',
    variables: {
      orderBy: 'targetDate',
      orderDirection: Direction.Asc,
      filters: [
        {
          filterOperation: FilterOperation.Equals,
          filterParameters: {
            field: 'product.id',
            value: salesOrderLine?.product?.id,
          },
        },
        {
          filterOperation: FilterOperation.Equals,
          filterParameters: {
            field: 'warehouse.id',
            value: salesOrderLine?.warehouse?.id,
          },
        },
        {
          filterOperation: FilterOperation.GreaterThan,
          filterParameters: {
            field: 'onOrder',
            value: '0',
          },
        },
        {
          filterOperation: FilterOperation.Equals,
          filterParameters: {
            field: 'purchaseOrder.archived',
            value: 'false',
          },
        },
      ],
    },
  });

  const { data: alternativeProductData } = useQuery<
    IAlternativeProductsDropdownQueryResult,
    IAlternativeProductsDropdownQueryVariables
  >(AlternativeProductsForProductListQuery, {
    variables: {
      productId: salesOrderLine?.product?.id,
      warehouseId: salesOrderLine?.warehouse?.id,
      quantityRequired: salesOrderLine?.quantity,
    },
    fetchPolicy: 'network-only',
    skip: !salesOrderLine,
  });

  const purchaseOrderLines = (purchaseOrderLinesData?.listData?.edges?.map((edge) => edge?.node) ||
    []) as Array<IPurchaseOrderLine>;
  const onOrder = purchaseOrderLines.reduce<number>((prev, next) => prev + next.onOrder, 0);

  const title = stockTotal?.product?.obsolete
    ? `Product ${salesOrderLine?.product?.reference} is obsolete`
    : `Short of ${short} x ${salesOrderLine?.product?.reference}`;

  const assemblyModalClose = () => {
    //close the modal
    setAssemblyModalOpen(false);
    setTempAssembly(null);
    onClose();
  };

  const assemblyModalSubmitSuccess = (result?: IAssembly): void => {
    //if we have successfully submitted the assembly draft
    if (salesOrderLine) {
      salesOrderLine.draftAssemblyId = result?.id;
    }
  };

  const orderButtons = (
    <>
      <CompoundButton
        secondaryText={'(Full Line Quantity)'}
        inline={true}
        onClick={getOnClick(true, false)}
        theme={InfoTheme}
        iconProps={componentsPristine ? undefined : fluentSaveIcon}
      >
        Direct Delivery
      </CompoundButton>
      <CompoundButton
        secondaryText={'(Shortage Only)'}
        inline={true}
        onClick={getOnClick(true, true)}
        theme={InfoTheme}
        iconProps={componentsPristine ? undefined : fluentSaveIcon}
      >
        Direct Delivery
      </CompoundButton>
      <CompoundButton
        secondaryText={'(Full Line Quantity)'}
        inline={true}
        onClick={getOnClick(false, false)}
        theme={InfoTheme}
        iconProps={componentsPristine ? undefined : fluentSaveIcon}
      >
        Back to Back (Purchase Order)
      </CompoundButton>
      <CompoundButton
        secondaryText={'(Shortage Only)'}
        inline={true}
        onClick={getOnClick(false, true)}
        theme={InfoTheme}
        iconProps={componentsPristine ? undefined : fluentSaveIcon}
      >
        Back to Back (Purchase Order)
      </CompoundButton>
    </>
  );

  return (
    <>
      {saveConfirmationPrompt}
      <AssemblyModal
        tempAssembly={tempAssembly}
        modalOpen={assemblyModalOpen}
        onSubmitSuccess={assemblyModalSubmitSuccess}
        onClose={assemblyModalClose}
      />
      <FluentModal isOpen={true} styles={{ main: { minWidth: '800px', paddingBottom: '60px' } }}>
        <BasicHeader>{title}</BasicHeader>
        <div style={{ padding: '1em' }}>
          <p>
            There are currently <strong>{freeStock}</strong> free, <strong>{makeable}</strong> makeable and{' '}
            <strong>{onOrder}</strong> on order of the original product.
          </p>

          {isAssembly ? null : (
            <Pivot>
              {stockTotal?.product?.obsolete ||
              (!orderConfig?.allowAllocationForOrderWithUnapprovedPrices &&
                (!salesOrderLine?.priceApproved ||
                  isPendingPriceApproval(salesOrder as ISalesOrder) ||
                  (manualPricingRequiresApproval && !salesOrder?.id))) ? null : (
                <PivotItem
                  headerText={`Assign Purchase Orders${
                    purchaseOrderLinesData?.listData ? ` (${purchaseOrderLinesData.listData.count})` : ''
                  }`}
                >
                  <AssignPurchaseOrdersGrid
                    purchaseOrderLines={purchaseOrderLines}
                    salesOrderLine={salesOrderLine}
                    rowIdx={rowIdx}
                    shortage={short}
                    ignoreKeypresses={purchaseOrderState?.modalOpen || assemblyModalOpen}
                  />
                </PivotItem>
              )}
              {checkLineCanProductChange(salesOrderLine) ? (
                <PivotItem
                  headerText={`Alternative Products${
                    alternativeProductData?.list ? ` (${alternativeProductData.list.length})` : ''
                  }`}
                >
                  <AlternativeProductsGrid
                    onRowClick={onRowClick}
                    onChange={onChange}
                    value={salesOrderLine}
                    alternativeProductData={alternativeProductData}
                    ignoreKeypresses={purchaseOrderState?.modalOpen || assemblyModalOpen}
                  />
                </PivotItem>
              ) : null}
            </Pivot>
          )}
          {fullyAssigned ||
          stockTotal?.product?.obsolete ||
          (!orderConfig?.allowAllocationForOrderWithUnapprovedPrices &&
            (!salesOrderLine?.priceApproved ||
              isPendingPriceApproval(salesOrder as ISalesOrder) ||
              (manualPricingRequiresApproval && !salesOrder?.id))) ? null : (
            <>
              <Form.Legend label={formLegend} styles={{ root: { marginRight: 0 } }} />
              <ButtonContainerDiv>
                {isAssembly && fullUserDetails?.flags?.assembliesEnabled ? (
                  <>
                    <CompoundButton
                      secondaryText={'(Full Line Quantity)'}
                      inline={true}
                      onClick={() => getRaiseAssemblyGetOnClick(false, false)}
                      theme={InfoTheme}
                    >
                      Back to Back (Assembly)
                    </CompoundButton>
                    <CompoundButton
                      secondaryText={'(Shortage Only)'}
                      inline={true}
                      onClick={() => getRaiseAssemblyGetOnClick(false, true)}
                      theme={InfoTheme}
                    >
                      Back to Back (Assembly)
                    </CompoundButton>
                  </>
                ) : null}
                {orderButtons}
                {editPurchaseOrderOnClick ? (
                  <CompoundButton
                    secondaryText={'(Pending Purchase Order)'}
                    inline={true}
                    onClick={editPurchaseOrderOnClick}
                    theme={InfoTheme}
                  >
                    Edit
                  </CompoundButton>
                ) : null}
              </ButtonContainerDiv>
              <Form.Legend label={'Shortages'} styles={{ root: { marginRight: 0 } }} />
              <ButtonContainerDiv>
                <InfoButton inline={true} onClick={() => setRaiseShortage(true)}>
                  Raise shortage
                </InfoButton>
                <InfoButton inline={true} onClick={onReviewLater}>
                  Review All Shortages at End
                </InfoButton>
              </ButtonContainerDiv>
            </>
          )}
          <ModalBottomButtonContainer>
            <WarningButton inline={true} onClick={onClose}>
              Continue
            </WarningButton>
          </ModalBottomButtonContainer>
        </div>
      </FluentModal>
      {purchaseOrderModal}
    </>
  );
};

export const SalesOrderAlternativeProductsModal = ({
  closeModal,
  formChange,
  salesOrder,
  showModal,
  rowIdx,
  short,
}: {
  closeModal: () => void;
  formChange: (fieldPath: string, value: any) => void;
  salesOrder: ISalesOrder;
  showModal: boolean;
  rowIdx?: number;
  short: number;
}) => {
  const onRaiseShortage = (supplier: ISupplier) => {
    Analytics.track('Raise Shortage');
    let orderLines = salesOrder.orderLines;
    orderLines = setIn(orderLines, [rowIdx, 'raiseShortage'], true);
    orderLines = setIn(orderLines, [rowIdx, 'shortageSupplier'], supplier);
    formChange('orderLines', orderLines);
    setRaiseShortage(false);
    closeModal();
  };

  const raiseAssemblyShortage = () => {
    Analytics.track('Raise Shortage');
    const orderLines = setIn(salesOrder.orderLines, [rowIdx, 'raiseShortage'], true);
    formChange('orderLines', orderLines);
    setRaiseShortage(false);
    closeModal();
  };

  const onReviewLater = () => {
    Analytics.track('Review Shortages Later');
    formChange('reviewShortagesLater', true);
    closeModal();
  };

  const [raiseShortage, setRaiseShortage] = useState(false);
  const [isAssembly, setIsAssembly] = useState<boolean>(false);

  useEffect(() => {
    if (salesOrder?.orderLines?.[rowIdx]?.product?.productClass === ProductClass.Assembly) {
      setIsAssembly(true);
    } else {
      setIsAssembly(false);
    }
  }, [salesOrder, rowIdx]);

  if (showModal) {
    if (raiseShortage) {
      if (isAssembly) {
        raiseAssemblyShortage();
        return null;
      }
      return (
        <Form.Field<any>
          dataid={`orderLines.${rowIdx}`}
          component={RaiseShortageSection}
          onClose={() => {
            setRaiseShortage(false);
            closeModal();
          }}
          onRaiseShortage={onRaiseShortage}
          onReviewLater={onReviewLater}
        />
      );
    }

    return (
      <Form.Field<IAlternativeProductModalProps>
        dataid={`orderLines.${rowIdx}`}
        rowIdx={rowIdx}
        component={AlternativeProductModal}
        temporaryOnChange={true}
        salesOrder={salesOrder}
        short={short}
        onClose={closeModal}
        onRowClick={closeModal}
        onReviewLater={onReviewLater}
        setRaiseShortage={setRaiseShortage}
        isAssembly={isAssembly}
      />
    );
  }
  return null;
};
